import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { searchAll } from "@/lib/api";
import { itemPath, CATEGORY_BY_TYPE } from "@/lib/categories";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Search, CornerDownLeft, BookMarked } from "lucide-react";

export default function SearchOverlay({ open, onOpenChange }) {
  const { t, L, lang, pickTitle, pickSecondary } = useLang();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [results, setResults] = useState({ content: [], glossary: [] });
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setQ("");
      setResults({ content: [], glossary: [] });
      setTimeout(() => inputRef.current?.focus(), 60);
    }
  }, [open]);

  useEffect(() => {
    if (!q.trim()) {
      setResults({ content: [], glossary: [] });
      return;
    }
    const id = setTimeout(async () => {
      setLoading(true);
      try {
        const data = await searchAll(q);
        setResults(data);
      } finally {
        setLoading(false);
      }
    }, 220);
    return () => clearTimeout(id);
  }, [q]);

  const go = useCallback((path) => {
    onOpenChange(false);
    navigate(path);
  }, [navigate, onOpenChange]);

  const submit = (e) => {
    e.preventDefault();
    if (q.trim()) go(`/search?q=${encodeURIComponent(q.trim())}`);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="search-overlay"
        className="top-[12%] max-h-[80vh] w-[94vw] max-w-2xl translate-y-0 gap-0 overflow-hidden rounded-none border-line bg-surface p-0"
      >
        <DialogTitle className="sr-only">Search Dharmartha</DialogTitle>
        <DialogDescription className="sr-only">Search mantras, kathas, puja vidhi, festivals and glossary terms.</DialogDescription>
        <form onSubmit={submit} className="flex items-center gap-3 border-b border-line px-5 py-5">
          <Search className="h-5 w-5 shrink-0 text-saffron" />
          <input
            ref={inputRef}
            data-testid="search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("search_placeholder")}
            className="w-full bg-transparent font-deva-body text-lg text-ink placeholder:text-ink-3 focus:outline-none"
          />
          {loading && <span className="text-xs text-ink-3">…</span>}
        </form>

        <div className="max-h-[58vh] overflow-y-auto">
          {q.trim() && !loading && results.content.length === 0 && results.glossary.length === 0 && (
            <p className="px-5 py-10 text-center text-sm text-ink-3">{t("no_results")}</p>
          )}

          {results.content.length > 0 && (
            <div className="py-2">
              <p className="overline-label px-5 pb-1 pt-3">{t("explore_knowledge")}</p>
              {results.content.map((it) => {
                const cat = CATEGORY_BY_TYPE[it.type];
                const title = pickTitle(it);
                const secondary = pickSecondary(it);
                return (
                  <button
                    key={it.slug}
                    data-testid={`search-result-${it.slug}`}
                    onClick={() => go(itemPath(it))}
                    className="flex w-full items-center justify-between gap-4 px-5 py-3 text-left transition-colors hover:bg-surface-alt"
                  >
                    <div className="min-w-0">
                      <p className={`truncate text-lg text-ink ${lang === "en" ? "font-serif-latin" : "font-deva"}`}>{title}</p>
                      <p className="truncate text-sm text-ink-3">{secondary ? `${secondary} · ` : ""}{cat?.en}</p>
                    </div>
                    <CornerDownLeft className="h-4 w-4 shrink-0 text-ink-3" />
                  </button>
                );
              })}
            </div>
          )}

          {results.glossary.length > 0 && (
            <div className="border-t border-line py-2">
              <p className="overline-label px-5 pb-1 pt-3">{t("nav_glossary")}</p>
              {results.glossary.map((g) => (
                <button
                  key={g.id}
                  onClick={() => go(`/glossary?q=${encodeURIComponent(g.transliteration)}`)}
                  className="flex w-full items-center gap-3 px-5 py-3 text-left transition-colors hover:bg-surface-alt"
                >
                  <BookMarked className="h-4 w-4 shrink-0 text-gold" />
                  <span className="font-deva text-base text-ink">{g.devanagari}</span>
                  <span className="text-sm text-ink-3">— {g.transliteration}</span>
                </button>
              ))}
            </div>
          )}

          {!q.trim() && (
            <p className="px-5 py-10 text-center font-deva-body text-sm text-ink-3">
              {t("search_placeholder")}
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
